import { useEffect, useState, useMemo, useRef } from 'react';
import { db, auth, useAuth } from '@archicore/shared';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { 
  Building2, Plus, Eye, LogOut, Shield, Users, 
  Briefcase, Activity, TrendingUp, Search, 
  RefreshCw, Zap, X, CheckCircle2, Cpu, HardDrive, Clock, Gift, Crown,
  AlertTriangle, DollarSign, Sparkles, Calculator, BarChart3, Calendar,
  ChevronDown, Filter
} from 'lucide-react';
import { signOut, getIdTokenResult } from 'firebase/auth';

// Emails autorizados para Poderes Maestros
const MASTER_EMAILS = ['contacto@archicoreanalytic.com', 'arqbonnel@gmail.com', 'master@archicore.app'];

interface Organization {
  id: string;
  name: string;
  status: string;
  plan: string;
  isTrial?: boolean;
  isConvenio?: boolean;
  activeUsersCount?: number;
  projectsCount?: number;
  archivedProjectsCount?: number;
  aiScansUsed?: number;
  monthlyAiScansUsed?: number;
  storageUsedMB?: number;
  excludeFromMRR?: boolean;
  customMonthlyPriceMXN?: number | null;
  createdAt?: any;
  suspendedAt?: any;
  trialEndsAt?: any;
  isDemo?: boolean;
  clientName?: string;
  paymentMethod?: string;
  billingNotes?: string;
  quotas?: {
    projectsLimit: number;
    usersLimit: number;
    monthlyAiScansLimit?: number;
    aiScansPerMonth?: number;
  };
}

const PLAN_PRICES_MXN: Record<string, number> = {
  inicio: 490,
  estudio: 890,
  standard: 890,
  basic: 890,
  constructora: 1690,
  premium: 1690,
  enterprise: 4990,
  trial: 0,
  free: 0
};

const PLAN_STORAGE_LIMIT_GB: Record<string, number> = {
  inicio: 5,
  estudio: 25,
  standard: 25,
  basic: 25,
  constructora: 100,
  premium: 100,
  enterprise: 500,
  trial: 1,
  free: 1
};

import TenantDetailsModal from '../components/TenantDetailsModal';
import SalesLeadsInbox from '../components/SalesLeadsInbox';
import RevenueSimulatorTab from '../components/RevenueSimulatorTab';
import LandingTrafficTab from '../components/LandingTrafficTab';

export default function DashboardPage() {
  const { user, initializing: authInitializing } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'organizations' | 'leads' | 'simulator' | 'traffic'>('organizations');
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgId, setNewOrgId] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState<any>(null);
  const [isMasterRole, setIsMasterRole] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [checkingMaster, setCheckingMaster] = useState(true);
  
  // Filtros Rápidos / Segmentación Comercial
  const [filterSegment, setFilterSegment] = useState<'all' | 'paying' | 'trial' | 'active_usage' | 'dormant' | 'courtesy' | 'expiring_soon' | 'ia_critical' | 'suspended'>('all');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!authInitializing) {
      if (user) {
        checkMasterRole();
        loadOrganizations();
      }
    }
  }, [user, authInitializing]);

  const checkMasterRole = async () => {
    if (!user) {
      setCheckingMaster(false);
      return;
    }
    
    const isMasterEmail = MASTER_EMAILS.includes(user.email || '');
    
    if (isMasterEmail) {
      try {
        if (typeof (user as any).getIdToken !== 'function') {
           console.log("[DashboardPage] Reintentando verificación con objeto User auténtico...");
           return; 
        }

        const token = await getIdTokenResult(user);
        let isMaster = token.claims.role === 'owner' && token.claims.orgId === 'archicore-master';
        
        if (!isMaster) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.role === 'owner' && data.orgId === 'archicore-master') {
              isMaster = true;
            }
          }
        }
        
        setIsMasterRole(isMaster);
      } catch (e) {
        console.error("Error checking master role:", e);
      }
    }

    setCheckingMaster(false);
  };

  const handleSelfActivate = async () => {
    if (!user || !MASTER_EMAILS.includes(user.email || '')) return;

    setIsActivating(true);
    try {
      const idToken = await user.getIdToken();
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'control-de-asistencia-en-obra';
      const url = `https://us-central1-${projectId}.cloudfunctions.net/fixMyRole`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Error sincronizando permisos');
      }

      await user.getIdToken(true);
      window.location.reload();
    } catch (err: any) {
      alert(`Error de activación: ${err.message}`);
    } finally {
      setIsActivating(false);
    }
  };

  const [isSyncing, setIsSyncing] = useState(false);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'organizations'));
      const orgs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Organization));
      setOrganizations(orgs);
      
      setTimeout(() => syncCounts(orgs), 100);
    } catch (e) {
      console.error('Error loading organizations:', e);
    } finally {
      setLoading(false);
    }
  };

  const syncCounts = async (orgList?: Organization[]) => {
    const listToSync = orgList || organizations;
    if (listToSync.length === 0) return;
    
    setIsSyncing(true);
    try {
      for (const org of listToSync) {
        // Count active projects (excluding system default like 'Oficina Central')
        const projsSnap = await getDocs(collection(db, 'organizations', org.id, 'obrasAreas'));
        const projectsCount = projsSnap.docs.filter(d => {
          const data = d.data();
          return !data.isSystemDefault && (data.status === 'active' || !data.status);
        }).length;
        const archivedProjectsCount = projsSnap.docs.filter(d => {
          const data = d.data();
          return !data.isSystemDefault && data.status === 'finished';
        }).length;

        // Count users (from the org-scoped collection)
        const usersSnap = await getDocs(collection(db, 'organizations', org.id, 'users'));
        const activeUsersCount = usersSnap.size;

        if (org.projectsCount !== projectsCount || org.activeUsersCount !== activeUsersCount || org.archivedProjectsCount !== archivedProjectsCount) {
          await updateDoc(doc(db, 'organizations', org.id), {
            projectsCount,
            archivedProjectsCount,
            activeUsersCount,
            updatedAt: new Date()
          });
        }
      }
      
      if (!orgList) await loadOrganizations();
      else {
        const updatedOrgs = await getDocs(collection(db, 'organizations'));
        setOrganizations(updatedOrgs.docs.map(d => ({ id: d.id, ...d.data() } as Organization)));
      }
    } catch (err: any) {
      console.error('Error syncing counts:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  // Filtrado reactivo por buscador y segmento comercial
  const filteredOrgs = useMemo(() => {
    return organizations.filter(org => {
      const matchesSearch = 
        (org.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (org.id || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;

      const isConvenio = org.paymentMethod === 'convenio' || Boolean(org.isConvenio);
      const isExcluded = Boolean(org.excludeFromMRR || org.id === 'bonnel' || org.name?.toLowerCase().includes('bonnel') || org.paymentMethod === 'courtesy' || isConvenio);
      const isTrial = (org.plan === 'trial' || org.plan === 'free' || org.isTrial || org.isDemo) && !isExcluded && !isConvenio;
      const isPaying = org.status === 'active' && !isTrial && !isExcluded;
      const isCourtesy = org.status === 'active' && isExcluded;
      const isSuspended = org.status === 'suspended';

      const trialEnd = org.trialEndsAt?.toDate ? org.trialEndsAt.toDate() : (org.trialEndsAt ? new Date(org.trialEndsAt) : null);
      const daysRemaining = trialEnd ? Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 999;
      const isExpiringSoon = isTrial && daysRemaining <= 7 && org.status === 'active';

      const aiUsed = Number(org.monthlyAiScansUsed || org.aiScansUsed || 0);
      const aiLimit = Number(org.quotas?.monthlyAiScansLimit || org.quotas?.aiScansPerMonth || 100);
      const isIaCritical = aiLimit > 0 && (aiUsed / aiLimit) >= 0.8;
      const hasRealUsage = aiUsed > 0 || (org.projectsCount || 0) > 0;

      if (filterSegment === 'paying') return isPaying;
      if (filterSegment === 'trial') return isTrial && !isSuspended;
      if (filterSegment === 'active_usage') return hasRealUsage && !isSuspended;
      if (filterSegment === 'dormant') return !hasRealUsage && !isSuspended;
      if (filterSegment === 'courtesy') return isCourtesy;
      if (filterSegment === 'expiring_soon') return isExpiringSoon;
      if (filterSegment === 'ia_critical') return isIaCritical;
      if (filterSegment === 'suspended') return isSuspended;

      return true;
    });
  }, [organizations, searchTerm, filterSegment]);

  // Métricas ejecutivas consolidadas
  const stats = useMemo(() => {
    const totalTenants = organizations.length;
    const activeTenants = organizations.filter(o => o.status === 'active').length;
    
    // Cuentas internas, de amigos o de cortesía/convenio excluidas del MRR
    const courtesyTenants = organizations.filter(o => 
      o.status === 'active' && 
      (o.excludeFromMRR || o.id === 'bonnel' || o.name?.toLowerCase().includes('bonnel') || o.paymentMethod === 'courtesy' || o.paymentMethod === 'convenio' || o.isConvenio)
    ).length;

    // Cuentas con pago real recurrente
    const payingTenants = organizations.filter(o => 
      o.status === 'active' && 
      !o.excludeFromMRR && 
      o.id !== 'bonnel' && 
      !o.name?.toLowerCase().includes('bonnel') && 
      o.paymentMethod !== 'courtesy' &&
      o.paymentMethod !== 'convenio' &&
      !o.isConvenio &&
      o.plan !== 'trial' && 
      o.plan !== 'free' && 
      !o.isTrial && 
      !o.isDemo
    ).length;

    // Clientes de prueba Trial activa (no excluidos ni convenios)
    const trialTenants = organizations.filter(o => 
      o.status === 'active' && 
      !o.excludeFromMRR && 
      o.id !== 'bonnel' && 
      !o.name?.toLowerCase().includes('bonnel') && 
      o.paymentMethod !== 'courtesy' &&
      o.paymentMethod !== 'convenio' &&
      !o.isConvenio &&
      (o.plan === 'trial' || o.plan === 'free' || o.isTrial || o.isDemo)
    ).length;

    const activeUsageTenants = organizations.filter(o => 
      o.status === 'active' && 
      (Number(o.monthlyAiScansUsed || o.aiScansUsed || 0) > 0 || (o.projectsCount || 0) > 0)
    ).length;

    const dormantTenants = organizations.filter(o => 
      o.status === 'active' && 
      Number(o.monthlyAiScansUsed || o.aiScansUsed || 0) === 0 && 
      (o.projectsCount || 0) === 0
    ).length;

    const suspendedTenants = organizations.filter(o => o.status === 'suspended').length;

    const totalProjects = organizations.reduce((acc, curr) => acc + (curr.projectsCount || 0), 0);
    const totalUsers = organizations.reduce((acc, curr) => acc + (curr.activeUsersCount || 0), 0);

    // MRR en MXN (Solo cuentas con pago real recurrente, excluyendo Bonnel, cortesías y convenios)
    const mrrMXN = organizations.reduce((acc, org) => {
      if (org.status !== 'active') return acc;
      if (org.excludeFromMRR || org.id === 'bonnel' || org.name?.toLowerCase().includes('bonnel') || org.paymentMethod === 'courtesy' || org.paymentMethod === 'convenio' || org.isConvenio) return acc;
      const isTrial = org.plan === 'trial' || org.plan === 'free' || org.isTrial || org.isDemo;
      if (isTrial) return acc;

      const price = (typeof org.customMonthlyPriceMXN === 'number' && org.customMonthlyPriceMXN > 0)
        ? org.customMonthlyPriceMXN
        : (PLAN_PRICES_MXN[org.plan?.toLowerCase()] || 0);
        
      return acc + price;
    }, 0);

    const mrrUSD = Math.round(mrrMXN / 20);
    const projectedARR_MXN = mrrMXN * 12;
    const projectedARR_USD = Math.round(projectedARR_MXN / 20);

    // Total Core IA Used
    const totalAiScansUsed = organizations.reduce((acc, org) => acc + Number(org.monthlyAiScansUsed || org.aiScansUsed || 0), 0);

    // Total Storage Used (MB)
    const totalStorageUsedMB = organizations.reduce((acc, org) => {
      const aiUsed = Number(org.monthlyAiScansUsed || org.aiScansUsed || 0);
      const limitGB = PLAN_STORAGE_LIMIT_GB[org.plan?.toLowerCase()] || 1;
      const usedMB = Number(org.storageUsedMB) || Math.min(Math.round((aiUsed * 0.15 + (org.projectsCount || 1) * 12) * 10) / 10, limitGB * 1024);
      return acc + usedMB;
    }, 0);

    return {
      totalTenants,
      activeTenants,
      payingTenants,
      trialTenants,
      activeUsageTenants,
      dormantTenants,
      courtesyTenants,
      suspendedTenants,
      totalProjects,
      totalUsers,
      mrrMXN,
      mrrUSD,
      projectedARR_MXN,
      projectedARR_USD,
      totalAiScansUsed,
      totalStorageUsedMB
    };
  }, [organizations]);

  const [newOrgPlan, setNewOrgPlan] = useState<string>('constructora');
  const [newOrgPaymentMethod, setNewOrgPaymentMethod] = useState<string>('transfer');
  const [newOrgBillingNotes, setNewOrgBillingNotes] = useState<string>('');

  const handleProvision = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProvisioning(true);
    setProvisionResult(null);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'control-de-asistencia-en-obra';
      const url = `https://us-central1-${projectId}.cloudfunctions.net/provisionOrganization`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          newOrgId,
          newOrgName,
          adminEmail,
          adminName: 'Admin',
          password: Math.random().toString(36).slice(-8) + 'Aa1!',
          plan: newOrgPlan,
          paymentMethod: newOrgPaymentMethod,
          billingNotes: newOrgBillingNotes
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error desconocido');

      setProvisionResult(data.data);
      loadOrganizations();
    } catch (err: any) {
      setProvisionResult({ error: err.message });
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleProvisionFromLead = (leadData: { company: string; email: string; suggestedPlan: string; leadId: string }) => {
    setNewOrgName(leadData.company || 'Nueva Empresa');
    const slug = (leadData.company || 'empresa')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    setNewOrgId(slug || 'cliente-' + Math.floor(Math.random() * 1000));
    setAdminEmail(leadData.email || '');
    if (leadData.suggestedPlan) {
      setNewOrgPlan(leadData.suggestedPlan);
    }
    setShowModal(true);
  };

  const handleLogout = async () => {
    localStorage.removeItem('archicore_cached_session');
    localStorage.removeItem('archicore_cached_profile');
    await signOut(auth);
    window.location.href = '/login';
  };

  const SEGMENT_OPTIONS: Array<{
    key: 'all' | 'paying' | 'trial' | 'active_usage' | 'dormant' | 'courtesy' | 'expiring_soon' | 'ia_critical' | 'suspended';
    label: string;
    icon: string;
    count?: number;
  }> = [
    { key: 'all', label: 'Todas las Organizaciones', icon: '🏢', count: organizations.length },
    { key: 'paying', label: 'Clientes de Pago', icon: '🟢', count: stats.payingTenants },
    { key: 'trial', label: 'En Prueba Trial', icon: '⏳', count: stats.trialTenants },
    { key: 'active_usage', label: 'En Uso Real (Activas)', icon: '🔥', count: stats.activeUsageTenants },
    { key: 'dormant', label: 'Cuentas Vacías (Sin uso)', icon: '💤', count: stats.dormantTenants },
    { key: 'courtesy', label: 'Cortesías / Convenios', icon: '🎁', count: stats.courtesyTenants },
    { key: 'expiring_soon', label: 'Próximas a Vencer (<7d)', icon: '⚠️' },
    { key: 'ia_critical', label: 'Cuota IA Crítica (>80%)', icon: '🧠' },
    { key: 'suspended', label: 'Suspendidas', icon: '🛑', count: stats.suspendedTenants }
  ];

  const currentSegment = SEGMENT_OPTIONS.find(o => o.key === filterSegment) || SEGMENT_OPTIONS[0];

  return (
    <div className="min-h-screen pb-20 bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0a2540] border-b border-emerald-900/20 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center">
              <img src="/Logos/LogoSolo.png" alt="ArchiCore Logo" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
                ArchiCore <span className="text-orange-500">Analytic</span>
                <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-full text-[10px] font-black uppercase tracking-wider">
                  Super Admin 2.0
                </span>
              </h1>
              <p className="text-[10px] uppercase tracking-widest text-slate-300 font-bold opacity-80">Torre de Control Empresarial</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10 text-xs font-bold text-slate-300">
              <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.5)]" />
              SISTEMAS OPERATIVOS
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              title="Cerrar Sesión"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-8">
        {/* Master Activation Banner (Safe Whitelist) */}
        {!checkingMaster && MASTER_EMAILS.includes(user?.email || '') && !isMasterRole && (
          <div className="mb-8 p-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl shadow-lg border border-orange-400 text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                <Shield size={32} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Activación de Superpoderes Requerida</h2>
                <p className="text-orange-50 text-sm">Detectamos tu cuenta Maestra. Sincroniza la autoridad base para crear y gestionar clientes.</p>
              </div>
            </div>
            <button
              onClick={handleSelfActivate}
              disabled={isActivating}
              className="px-8 py-3 bg-white text-orange-600 font-bold rounded-xl shadow-xl hover:bg-orange-50 transition-all flex items-center gap-3 whitespace-nowrap active:scale-95 disabled:opacity-50"
            >
              {isActivating ? (
                <>
                  <RefreshCw size={20} className="animate-spin" />
                  <span>ACTIVANDO...</span>
                </>
              ) : (
                <>
                  <Activity size={20} />
                  <span>ACTIVAR ACCESO MAESTRO</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Banner de Cupos Fundadores (Meta de 50) */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-[#0a2540] to-slate-900 border border-cyan-500/30 text-white flex flex-col md:flex-row items-center justify-between gap-4 shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-cyan-500/20 text-[#00D8EE] border border-cyan-500/30 shrink-0">
              <Sparkles size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm text-white">Campaña Beta: 50 Cupos Fundadores</h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-500/20 text-[#00D8EE] border border-cyan-500/40">
                  {stats.trialTenants} / 50 Ocupados
                </span>
              </div>
              <p className="text-xs text-slate-300">
                {Math.max(0, 50 - stats.trialTenants)} cupos restantes disponibles de cortesía (30 días de prueba + 100 escaneos IA).
              </p>
            </div>
          </div>
          <div className="w-full md:w-64 space-y-1 shrink-0">
            <div className="flex justify-between text-[11px] font-mono text-slate-300">
              <span>Progreso de Meta</span>
              <span className="font-bold text-[#00D8EE]">{Math.min(100, Math.round((stats.trialTenants / 50) * 100))}%</span>
            </div>
            <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden border border-white/10">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(0,216,238,0.5)]"
                style={{ width: `${Math.min(100, Math.round((stats.trialTenants / 50) * 100))}%` }}
              />
            </div>
          </div>
        </div>

        {/* Executive KPI Grid (5 Tarjetas de Negocio) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {/* Card 1: MRR Real */}
          <div className="p-4 rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50/80 to-amber-50/40 shadow-xs relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2 bg-orange-500 text-white rounded-xl shadow-sm">
                <DollarSign size={18} />
              </div>
              <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                MRR Mensual
              </span>
            </div>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Ingresos Facturados</p>
            <h4 className="text-xl font-black text-slate-900 mt-0.5">
              ${stats.mrrMXN.toLocaleString()} <span className="text-xs font-bold text-slate-500">MXN</span>
            </h4>
            <p className="text-[11px] text-slate-500 mt-1 font-medium">
              ≈ ${stats.mrrUSD.toLocaleString()} USD/mes ({stats.payingTenants} de pago)
            </p>
          </div>

          {/* Card 2: Proyección ARR Anual */}
          <div className="p-4 rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-blue-50/40 shadow-xs relative overflow-hidden">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-sm">
                <TrendingUp size={18} />
              </div>
              <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                ARR Anual
              </span>
            </div>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Proyección a 12 Meses</p>
            <h4 className="text-xl font-black text-slate-900 mt-0.5">
              ${stats.projectedARR_MXN.toLocaleString()} <span className="text-xs font-bold text-slate-500">MXN</span>
            </h4>
            <p className="text-[11px] text-slate-500 mt-1 font-medium">
              ≈ ${stats.projectedARR_USD.toLocaleString()} USD/año estimado
            </p>
          </div>

          {/* Card 3: Clientes & Conversión */}
          <div className="p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2 bg-cyan-50 text-cyan-600 border border-cyan-100 rounded-xl">
                <Building2 size={18} />
              </div>
              <span className="text-[10px] bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                {stats.payingTenants} de pago
              </span>
            </div>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Empresas Activas</p>
            <h4 className="text-xl font-black text-slate-900 mt-0.5">
              {stats.activeTenants} <span className="text-xs font-normal text-slate-400">/ {stats.totalTenants} totales</span>
            </h4>
            <p className="text-[11px] text-slate-500 mt-1 font-medium">
              {stats.trialTenants} trial • {stats.courtesyTenants} cortesías
            </p>
          </div>

          {/* Card 4: Core IA Consumido */}
          <div className="p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2 bg-purple-50 text-purple-600 border border-purple-100 rounded-xl">
                <Cpu size={18} />
              </div>
              <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                Core IA
              </span>
            </div>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Digitalizaciones Mes</p>
            <h4 className="text-xl font-black text-slate-900 mt-0.5">
              {stats.totalAiScansUsed.toLocaleString()} <span className="text-xs font-normal text-slate-400">procesamientos</span>
            </h4>
            <p className="text-[11px] text-slate-500 mt-1 font-medium">
              Comprobantes y remitos
            </p>
          </div>

          {/* Card 5: Nube Cloud Global */}
          <div className="p-4 rounded-2xl border border-slate-200 bg-white shadow-xs">
            <div className="flex justify-between items-start mb-2">
              <div className="p-2 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl">
                <HardDrive size={18} />
              </div>
              <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                Storage
              </span>
            </div>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">Almacenamiento Global</p>
            <h4 className="text-xl font-black text-slate-900 mt-0.5">
              {(stats.totalStorageUsedMB / 1024).toFixed(2)} <span className="text-xs font-bold text-slate-500">GB</span>
            </h4>
            <p className="text-[11px] text-slate-500 mt-1 font-medium">
              {stats.totalProjects} obras • {stats.totalUsers} usuarios
            </p>
          </div>
        </div>

        {/* Navigation Tabs: Organizaciones vs Leads vs Simulador */}
        <div className="flex items-center gap-2 mb-6 border-b border-slate-200 pb-3 overflow-x-auto">
          <button
            onClick={() => setActiveTab('organizations')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'organizations'
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Building2 size={16} />
            Organizaciones ({organizations.length})
          </button>

          <button
            onClick={() => setActiveTab('leads')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'leads'
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <TrendingUp size={16} className={activeTab === 'leads' ? 'text-orange-400' : 'text-slate-400'} />
            Bandeja de Ventas / Leads
          </button>

          <button
            onClick={() => setActiveTab('simulator')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'simulator'
                ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Calculator size={16} className={activeTab === 'simulator' ? 'text-white' : 'text-orange-500'} />
            <span>🚀 Simulador de Crecimiento</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
              activeTab === 'simulator' ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-700'
            }`}>
              Metas
            </span>
          </button>

          <button
            onClick={() => setActiveTab('traffic')}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'traffic'
                ? 'bg-slate-900 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <BarChart3 size={16} className={activeTab === 'traffic' ? 'text-cyan-400' : 'text-slate-400'} />
            <span>📊 Tráfico & Visitas Landing</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
              activeTab === 'traffic' ? 'bg-cyan-500/30 text-cyan-300' : 'bg-cyan-100 text-cyan-800'
            }`}>
              En Vivo
            </span>
          </button>
        </div>

        {activeTab === 'leads' ? (
          <SalesLeadsInbox
            onProvisionRealClient={handleProvisionFromLead}
          />
        ) : activeTab === 'simulator' ? (
          <RevenueSimulatorTab
            currentPayingTenants={stats.payingTenants}
            currentInicioCount={organizations.filter(o => o.status === 'active' && o.plan === 'inicio' && !o.excludeFromMRR && o.paymentMethod !== 'convenio' && !o.isConvenio && o.id !== 'bonnel' && !o.name?.toLowerCase().includes('bonnel')).length}
            currentEstudioCount={organizations.filter(o => o.status === 'active' && (o.plan === 'estudio' || o.plan === 'standard') && !o.excludeFromMRR && o.paymentMethod !== 'convenio' && !o.isConvenio && o.id !== 'bonnel' && !o.name?.toLowerCase().includes('bonnel')).length}
            currentConstructoraCount={organizations.filter(o => o.status === 'active' && (o.plan === 'constructora' || o.plan === 'premium') && !o.excludeFromMRR && o.paymentMethod !== 'convenio' && !o.isConvenio && o.id !== 'bonnel' && !o.name?.toLowerCase().includes('bonnel')).length}
            currentEnterpriseCount={organizations.filter(o => o.status === 'active' && o.plan === 'enterprise' && !o.excludeFromMRR && o.paymentMethod !== 'convenio' && !o.isConvenio && o.id !== 'bonnel' && !o.name?.toLowerCase().includes('bonnel')).length}
          />
        ) : activeTab === 'traffic' ? (
          <LandingTrafficTab />
        ) : (
          <>
            {/* Search, Filter & Action Bar */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-4 gap-3">
              {/* Buscador y Dropdown de Segmentación */}
              <div className="flex items-center gap-2.5 flex-1 max-w-2xl">
                {/* Search Input */}
                <div className="relative group flex-1">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-600 transition-colors" size={18} />
                  <input 
                    type="text" 
                    placeholder="Buscar por empresa o ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all shadow-xs h-10 text-slate-800"
                  />
                </div>

                {/* Dropdown de Filtro por Segmento */}
                <div className="relative" ref={filterDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                    className={`h-10 flex items-center gap-2 px-3.5 rounded-xl text-xs font-bold transition-all border shrink-0 shadow-xs ${
                      filterSegment !== 'all'
                        ? 'bg-cyan-50 border-cyan-300 text-cyan-900 ring-2 ring-cyan-500/10'
                        : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700'
                    }`}
                  >
                    <Filter size={14} className={filterSegment !== 'all' ? 'text-cyan-600' : 'text-slate-400'} />
                    <span>{currentSegment.icon}</span>
                    <span className="max-w-[140px] truncate">{currentSegment.label}</span>
                    {currentSegment.count !== undefined && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold ${
                        filterSegment !== 'all' ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {currentSegment.count}
                      </span>
                    )}
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Menú Desplegable con Opciones */}
                  {isFilterDropdownOpen && (
                    <div className="absolute left-0 sm:right-0 sm:left-auto top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl z-50 p-2 space-y-1 animate-in fade-in zoom-in-95 duration-150">
                      <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-between border-b border-slate-100 pb-2 mb-1">
                        <span>Filtrar Organizaciones</span>
                        {filterSegment !== 'all' && (
                          <button
                            type="button"
                            onClick={() => {
                              setFilterSegment('all');
                              setIsFilterDropdownOpen(false);
                            }}
                            className="text-cyan-600 hover:text-cyan-800 text-[10px] font-bold lowercase"
                          >
                            restablecer
                          </button>
                        )}
                      </div>

                      <div className="max-h-80 overflow-y-auto space-y-0.5 pr-1">
                        {SEGMENT_OPTIONS.map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => {
                              setFilterSegment(opt.key);
                              setIsFilterDropdownOpen(false);
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-all text-left ${
                              filterSegment === opt.key
                                ? 'bg-cyan-50 text-cyan-950 font-bold border border-cyan-200'
                                : 'text-slate-700 hover:bg-slate-50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <span>{opt.icon}</span>
                              <span className="truncate">{opt.label}</span>
                            </span>
                            {opt.count !== undefined && (
                              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-bold shrink-0 ${
                                filterSegment === opt.key ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {opt.count}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                <button 
                  onClick={() => { loadOrganizations(); syncCounts(); }}
                  disabled={isSyncing || loading}
                  className="px-3.5 h-10 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-50 transition-all shadow-xs text-xs disabled:opacity-50"
                  title="Sincronizar contadores"
                >
                  <RefreshCw className={isSyncing || loading ? 'animate-spin text-cyan-600' : 'text-slate-500'} size={15} />
                  <span>{isSyncing ? 'Sincronizando...' : 'Sincronizar'}</span>
                </button>

                <button 
                  onClick={() => setShowModal(true)}
                  className="px-4 h-10 bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white rounded-xl font-bold flex items-center gap-2 shadow-sm transition-all text-xs whitespace-nowrap"
                >
                  <Plus size={16} />
                  <span>+ Nuevo Cliente</span>
                </button>
              </div>
            </div>

            {/* Pastilla de Filtro Activo (fácil de remover con un clic) */}
            {filterSegment !== 'all' && (
              <div className="flex items-center gap-2 mb-4 px-1">
                <span className="text-xs text-slate-400">Filtrando por:</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 text-cyan-800 border border-cyan-200 shadow-xs">
                  <span>{currentSegment.icon}</span>
                  <span>{currentSegment.label}</span>
                  <button
                    type="button"
                    onClick={() => setFilterSegment('all')}
                    className="ml-1 p-0.5 hover:bg-cyan-200/50 rounded-full text-cyan-700"
                    title="Quitar filtro"
                  >
                    <X size={12} />
                  </button>
                </span>
                <span className="text-xs text-slate-400 font-mono">({filteredOrgs.length} {filteredOrgs.length === 1 ? 'empresa' : 'empresas'})</span>
              </div>
            )}

            {/* Organizations Table (Torre de Control 2.0) */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-12">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200">
                      <th className="px-5 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Empresa / Tenant</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Plan & Vigencia</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500">Estado</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Core IA (Mes)</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Storage Cloud</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Obras</th>
                      <th className="px-4 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">Usuarios</th>
                      <th className="px-5 py-3.5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">Gestión</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {loading ? (
                      Array(5).fill(0).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td colSpan={8} className="px-5 py-4">
                            <div className="h-10 bg-slate-100 rounded-lg w-full" />
                          </td>
                        </tr>
                      ))
                    ) : filteredOrgs.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-16 text-center">
                          <div className="flex flex-col items-center">
                            <Search size={36} className="text-slate-300 mb-2" />
                            <p className="text-slate-500 font-bold">No se encontraron organizaciones en este segmento</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredOrgs.map(org => {
                        const isConvenio = org.paymentMethod === 'convenio' || Boolean(org.isConvenio);
                        const isExcluded = Boolean(org.excludeFromMRR || org.id === 'bonnel' || org.name?.toLowerCase().includes('bonnel') || org.paymentMethod === 'courtesy' || isConvenio);
                        const isTrial = (org.plan === 'trial' || org.plan === 'free' || org.isTrial || org.isDemo) && !isExcluded && !isConvenio;
                        const trialEnd = org.trialEndsAt?.toDate ? org.trialEndsAt.toDate() : (org.trialEndsAt ? new Date(org.trialEndsAt) : null);
                        const daysLeft = trialEnd ? Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

                        const aiUsed = Number(org.monthlyAiScansUsed || org.aiScansUsed || 0);
                        const aiLimit = Number(org.quotas?.monthlyAiScansLimit || org.quotas?.aiScansPerMonth || 100);
                        const aiPercentage = Math.round((aiUsed / aiLimit) * 100);

                        const storageLimitGB = PLAN_STORAGE_LIMIT_GB[org.plan?.toLowerCase()] || 1;
                        const storageUsedMB = Number(org.storageUsedMB) || Math.min(Math.round((aiUsed * 0.15 + (org.projectsCount || 1) * 12) * 10) / 10, storageLimitGB * 1024);
                        const storagePercentage = Math.round((storageUsedMB / (storageLimitGB * 1024)) * 100);

                        const activeProjects = Number(org.projectsCount || 0);
                        const projectsLimit = Number(org.quotas?.projectsLimit || 1);

                        const activeUsers = Number(org.activeUsersCount || 1);
                        const usersLimit = Number(org.quotas?.usersLimit || 3);

                        return (
                          <tr 
                            key={org.id} 
                            className="hover:bg-slate-50/60 transition-colors group cursor-pointer"
                            onClick={() => setSelectedOrg(org)}
                          >
                            {/* 1. Empresa / Tenant */}
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-xl border ${
                                  org.status === 'active' ? 'bg-cyan-50 border-cyan-100 text-cyan-600' : 'bg-slate-100 border-slate-200 text-slate-400'
                                }`}>
                                  <Building2 size={16} />
                                </div>
                                <div>
                                  <p className="font-bold text-slate-900 text-sm leading-tight flex items-center gap-1.5 flex-wrap">
                                    <span>{org.name}</span>
                                    {isConvenio ? (
                                      <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 uppercase tracking-tight flex items-center gap-1">
                                        🤝 Convenio 1 Año
                                      </span>
                                    ) : isExcluded ? (
                                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 uppercase tracking-tight">
                                        Cortesía
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className="text-[10px] font-mono text-slate-400 uppercase tracking-tighter">{org.id}</p>
                                </div>
                              </div>
                            </td>

                            {/* 2. Plan & Vigencia */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col items-start gap-1">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                                  org.plan === 'constructora' || org.plan === 'premium' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                  org.plan === 'estudio' || org.plan === 'standard' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                  org.plan === 'inicio' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                                  org.plan === 'enterprise' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  'bg-amber-50 text-amber-800 border-amber-200'
                                }`}>
                                  {isTrial ? <Clock size={10} /> : <Zap size={10} className="fill-current" />}
                                  {org.plan}
                                </span>

                                {isConvenio ? (
                                  <span className="text-[10px] font-extrabold text-indigo-600 flex items-center gap-1">
                                    <Calendar size={10} />
                                    {daysLeft !== null ? `${daysLeft}d convenio` : 'Convenio 1a'}
                                  </span>
                                ) : isTrial ? (
                                  <span className={`text-[10px] font-bold ${
                                    daysLeft !== null && daysLeft <= 3 ? 'text-red-600 animate-pulse' :
                                    daysLeft !== null && daysLeft <= 7 ? 'text-amber-600' : 'text-slate-400'
                                  }`}>
                                    {daysLeft !== null ? (daysLeft > 0 ? `${daysLeft}d restantes` : 'Trial vencido') : 'Sin límite'}
                                  </span>
                                ) : null}
                              </div>
                            </td>

                            {/* 3. Estado */}
                            <td className="px-4 py-3.5">
                              {org.status === 'suspended' ? (
                                (() => {
                                  const suspendedDate = org.suspendedAt?.toDate ? org.suspendedAt.toDate() : (org.suspendedAt ? new Date(org.suspendedAt) : null);
                                  const daysSuspended = suspendedDate ? Math.floor((Date.now() - suspendedDate.getTime()) / (1000 * 60 * 60 * 24)) : 0;
                                  const daysRemaining = Math.max(0, 30 - daysSuspended);

                                  return (
                                    <div className="flex flex-col items-start gap-0.5">
                                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-red-100 text-red-700 border border-red-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                                        Suspendido
                                      </span>
                                      <span className="text-[10px] text-slate-500 font-medium">
                                        {daysSuspended}d susp. ({daysRemaining}d gracia)
                                      </span>
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    {isConvenio ? 'Convenio Activo' : isTrial ? 'Trial Activo' : 'Activo'}
                                  </span>
                                  {aiUsed > 0 || activeProjects > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50/80 px-1.5 py-0.5 rounded border border-emerald-200">
                                      🔥 En Uso Real
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                      💤 Sin Actividad
                                    </span>
                                  )}
                                </div>
                              )}
                            </td>

                            {/* 4. Core IA (Mes) */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1.5 font-bold text-xs">
                                  <span className={aiPercentage >= 80 ? 'text-purple-700 font-black' : 'text-slate-800'}>{aiUsed}</span>
                                  <span className="text-slate-300">/</span>
                                  <span className="text-slate-400">{aiLimit}</span>
                                </div>
                                <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all ${
                                      aiPercentage >= 100 ? 'bg-red-500' :
                                      aiPercentage >= 80 ? 'bg-amber-500' : 'bg-purple-500'
                                    }`}
                                    style={{ width: `${Math.min(aiPercentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* 5. Storage Cloud */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1 text-xs font-bold text-slate-700">
                                  <span>{storageUsedMB}</span>
                                  <span className="text-[10px] text-slate-400 font-normal">MB / {storageLimitGB}GB</span>
                                </div>
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-emerald-500 transition-all"
                                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* 6. Obras Activas */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                  <span>{activeProjects}</span>
                                  <span className="text-slate-300">/</span>
                                  <span className="text-slate-400">{projectsLimit}</span>
                                </div>
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-cyan-500" 
                                    style={{ width: `${Math.min((activeProjects / projectsLimit) * 100, 100)}%` }}
                                  />
                                </div>
                                {org.archivedProjectsCount ? (
                                  <span className="text-[9px] text-slate-400 font-medium">+{org.archivedProjectsCount} arch.</span>
                                ) : null}
                              </div>
                            </td>

                            {/* 7. Usuarios */}
                            <td className="px-4 py-3.5">
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                  <span>{activeUsers}</span>
                                  <span className="text-slate-300">/</span>
                                  <span className="text-slate-400">{usersLimit}</span>
                                </div>
                                <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-amber-500" 
                                    style={{ width: `${Math.min((activeUsers / usersLimit) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>

                            {/* 8. Acciones Rápidas */}
                            <td className="px-5 py-3.5 text-right">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOrg(org);
                                }}
                                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition-colors inline-flex items-center gap-1 shadow-xs"
                                title="Ver detalles y gestionar cortesías"
                              >
                                <Eye size={13} />
                                <span>Ver</span>
                              </button>
                            </td>

                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Tenant Details Modal */}
      {selectedOrg && (
        <TenantDetailsModal 
          org={selectedOrg} 
          onClose={() => setSelectedOrg(null)}
          onUpdate={(data) => {
            setSelectedOrg(null);
            loadOrganizations();
          }}
        />
      )}

      {/* Provision Modal (Alta de Cliente Real) */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-cyan-500 via-orange-500 to-indigo-600" />

            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-cyan-50 text-cyan-600 rounded-xl border border-cyan-100">
                  <Building2 size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Alta de Nuevo Cliente / Tenant</h3>
                  <p className="text-xs text-slate-500">Crea la empresa en Firestore y asigna su plan comercial</p>
                </div>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-8">
              {provisionResult ? (
                <div className={`p-6 rounded-2xl ${provisionResult.error ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  {provisionResult.error ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 text-red-600">
                        <Activity size={24} />
                        <h4 className="font-bold text-base">Error al Crear Organización</h4>
                      </div>
                      <p className="text-xs text-red-700 font-mono bg-white/60 p-3 rounded-xl border border-red-200">{provisionResult.error}</p>
                      <button
                        onClick={() => setProvisionResult(null)}
                        className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-xs transition-colors"
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 text-emerald-700">
                        <CheckCircle2 size={26} className="text-emerald-600" />
                        <div>
                          <h4 className="font-bold text-lg">¡Cliente Dado de Alta Exitosamente!</h4>
                          <p className="text-xs text-emerald-600">Organización y usuario administrador creados en Firebase.</p>
                        </div>
                      </div>

                      <div className="bg-white p-5 rounded-2xl border border-emerald-200 shadow-sm space-y-4 text-xs">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Correo de Acceso del Administrador</span>
                          <span className="font-mono font-bold text-slate-900 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 block">{provisionResult.email}</span>
                        </div>
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Contraseña Temporal Generada</span>
                          <span className="font-mono font-extrabold text-base text-cyan-700 bg-cyan-50 px-3 py-1.5 rounded-lg border border-cyan-200 block select-all">
                            {provisionResult.tempPassword}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setShowModal(false);
                          setProvisionResult(null);
                          setNewOrgName('');
                          setNewOrgId('');
                          setAdminEmail('');
                        }}
                        className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs shadow-md transition-colors"
                      >
                        Finalizar y Volver al Dashboard
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleProvision} className="space-y-5">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Nombre de la Empresa</label>
                        <input
                          type="text"
                          className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none"
                          placeholder="Ej. Constructora Alexander"
                          value={newOrgName}
                          onChange={e => {
                            setNewOrgName(e.target.value);
                            setNewOrgId(
                              e.target.value
                                .toLowerCase()
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .replace(/[^a-z0-9]+/g, '-')
                                .replace(/^-|-$/g, '')
                            );
                          }}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">ID Identificador (Slug)</label>
                        <input
                          type="text"
                          className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none"
                          placeholder="ej. alexander-constructora"
                          value={newOrgId}
                          onChange={e => setNewOrgId(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Email del Administrador</label>
                      <input
                        type="email"
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none"
                        placeholder="admin@constructora-alexander.com"
                        value={adminEmail}
                        onChange={e => setAdminEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Plan Contratado</label>
                        <select
                          value={newOrgPlan}
                          onChange={e => setNewOrgPlan(e.target.value)}
                          className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500 outline-none capitalize"
                        >
                          <option value="inicio">Plan Inicio ($490 MXN • 3 us / 2 obras / 500 IA)</option>
                          <option value="estudio">Plan Estudio ($890 MXN • 10 us / 5 obras / 1,500 IA)</option>
                          <option value="constructora">Plan Constructora ($1,690 MXN • 30 us / 15 obras / 5,000 IA)</option>
                          <option value="enterprise">Plan a Medida / Corporativo (Personalizado)</option>
                          <option value="trial">Periodo de Prueba (30 Días • 3 us / 2 obras / 100 IA)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Método de Pago</label>
                        <select
                          value={newOrgPaymentMethod}
                          onChange={e => setNewOrgPaymentMethod(e.target.value)}
                          className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 outline-none"
                        >
                          <option value="transfer">Transferencia Bancaria (SPEI)</option>
                          <option value="card">Tarjeta de Crédito / Débito</option>
                          <option value="convenio">Convenio Anual (1 Año Feedback)</option>
                          <option value="courtesy">Cortesía / Prueba Extendida</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Notas de Facturación / Referencia</label>
                      <input
                        type="text"
                        placeholder="Ej. Pago verificado por SPEI fol #98231"
                        value={newOrgBillingNotes}
                        onChange={e => setNewOrgBillingNotes(e.target.value)}
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isProvisioning}
                      className="px-6 py-2.5 bg-gradient-to-r from-cyan-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      {isProvisioning ? <RefreshCw className="animate-spin" size={16} /> : <Shield size={16} />}
                      <span>{isProvisioning ? 'Creando...' : 'Crear Organización y Enviar Acceso'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



